/**
 * Turns an estimate or a vehicle into the flat set of numbers the equations read (plan §5.2).
 *
 * The bundle types here are **plain data the engine defines**: the service maps its drizzle rows into
 * them and the browser builds one from the form it is holding, so neither side needs the other's types
 * and this file can be unit-tested with object literals.
 *
 * Three rules are load-bearing:
 *
 *  - **Unknown is not zero.** An inspection selection that is still `UNKNOWN`, or a facelift/customs
 *    flag nobody has answered, produces *no input at all*. The run then fails those equations with
 *    `MISSING_INPUT` and the estimator shows "incomplete" instead of quietly pricing the vehicle as if
 *    it were excellent (PP AC03).
 *  - **Not applicable is zero, and says so.** A petrol vehicle has no hybrid assessment: `hybridRate`
 *    is 0 with a `NOT_APPLICABLE` provenance, which is what the UI prints (PP §7.5).
 *  - **Overrides win, visibly.** A value the record overrides is marked `OVERRIDE`, so the estimator can
 *    show which figures came from the template and which from this vehicle (PP AC01).
 */
import { resolveCosts, resolvedReconditioning } from '../costs/resolution.js';
import { roundToScale } from '../money.js';
import { DEFAULT_OVERRIDABLE_KEYS, gradeParamKey, severityParamKey } from '../templates/param-catalog.js';
const DAY_MS = 86_400_000;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
/** Parses `YYYY-MM-DD` as a UTC midnight, so day differences never drift with the local timezone. */
export const parseIsoDate = (value) => {
    if (!value || !ISO_DATE.test(value)) {
        return null;
    }
    const parsed = Date.parse(`${value}T00:00:00.000Z`);
    return Number.isNaN(parsed) ? null : parsed;
};
export const daysBetween = (from, to) => Math.floor((to - from) / DAY_MS);
export const buildValuationInputs = (bundle) => {
    const { facts, inspection, template } = bundle;
    const params = template.params;
    const overridable = new Set(template.overridableKeys ?? DEFAULT_OVERRIDABLE_KEYS);
    const overrides = bundle.overrides ?? {};
    const inputs = {};
    const provenance = {};
    const missing = [];
    const blocking = [];
    const set = (name, value, source, note) => {
        if (value === null || value === undefined || !Number.isFinite(value)) {
            return;
        }
        inputs[name] = value;
        provenance[name] = note === undefined ? { source } : { source, note };
    };
    const miss = (name, field, detail) => {
        missing.push({ name, field, detail });
    };
    /** A template value, unless the record overrides it (and is allowed to). */
    const fromTemplate = (name, key = name) => {
        const override = overrides[key];
        if (override !== null && override !== undefined && Number.isFinite(override) && overridable.has(key)) {
            set(name, override, 'OVERRIDE', `Overrides the template value ${params[key] ?? '—'}`);
            return;
        }
        set(name, params[key], 'TEMPLATE');
    };
    const valuationAt = parseIsoDate(bundle.valuationDate);
    if (valuationAt === null) {
        blocking.push({ code: 'INVALID_DATE', field: 'valuationDate', detail: 'The valuation date is not a valid calendar date' });
    }
    const valuationYear = valuationAt === null ? null : new Date(valuationAt).getUTCFullYear();
    set('valuationYear', valuationYear, 'VEHICLE');
    // ── identity facts ────────────────────────────────────────────────────────────────────────────
    if (facts.modelYear === null || facts.modelYear === undefined) {
        miss('modelYear', 'modelYear', 'The model year is not recorded');
    }
    else {
        set('modelYear', facts.modelYear, 'VEHICLE');
        if (valuationYear !== null && facts.modelYear > valuationYear) {
            blocking.push({
                code: 'UNSUPPORTED_MODEL_YEAR',
                field: 'modelYear',
                detail: 'A model year later than the valuation year needs an explicit age policy before a final calculation (PP §7.5)',
            });
        }
    }
    if (facts.referencePrice === null || facts.referencePrice === undefined) {
        miss('referencePrice', 'referencePrice', 'The reference price is not recorded');
    }
    else if (facts.referencePrice <= 0) {
        blocking.push({ code: 'INVALID_REFERENCE_PRICE', field: 'referencePrice', detail: 'The reference price must be greater than zero' });
    }
    else {
        set('referencePrice', facts.referencePrice, 'VEHICLE');
    }
    if (facts.odometerKm === null || facts.odometerKm === undefined) {
        miss('odometer', 'odometerKm', 'The odometer reading is not recorded');
    }
    else if (facts.odometerKm < 0) {
        blocking.push({ code: 'NEGATIVE_ODOMETER', field: 'odometerKm', detail: 'The odometer reading cannot be negative' });
    }
    else {
        set('odometer', facts.odometerKm, 'VEHICLE');
    }
    if (facts.ownerCount === null || facts.ownerCount === undefined) {
        miss('ownerCount', 'ownerCount', 'The number of owners is not recorded');
    }
    else if (facts.ownerCount < 0) {
        blocking.push({ code: 'NEGATIVE_OWNERS', field: 'ownerCount', detail: 'The number of owners cannot be negative' });
    }
    else if (!Number.isInteger(facts.ownerCount)) {
        blocking.push({ code: 'NON_INTEGER_OWNERS', field: 'ownerCount', detail: 'The number of owners must be a whole number' });
    }
    else {
        set('ownerCount', facts.ownerCount, 'VEHICLE');
    }
    const registrationAt = parseIsoDate(facts.registrationExpiry);
    if (facts.registrationExpiry && registrationAt === null) {
        blocking.push({ code: 'INVALID_DATE', field: 'registrationExpiry', detail: 'The registration expiry is not a valid calendar date' });
    }
    else if (registrationAt === null || valuationAt === null) {
        miss('registrationDaysDifference', 'registrationExpiry', 'The registration expiry date is not recorded');
    }
    else {
        set('registrationDaysDifference', daysBetween(valuationAt, registrationAt), 'VEHICLE');
    }
    // ── inspection ────────────────────────────────────────────────────────────────────────────────
    if (inspection.grade7 === 'UNKNOWN') {
        miss('chassisRate', 'grade7', 'The سبعة جيد grade has not been recorded; an unknown grade is never treated as no deduction');
    }
    else {
        set('chassisRate', params[gradeParamKey(inspection.grade7)], 'VEHICLE', `Grade ${inspection.grade7} in the template’s grade table`);
    }
    const severityInput = (name, field, severity, label) => {
        if (severity === 'UNKNOWN') {
            miss(name, field, `The ${label} condition has not been recorded; unknown is never treated as Excellent`);
            return;
        }
        set(name, params[severityParamKey(severity)], 'VEHICLE', `${severity} in the template’s severity table`);
    };
    severityInput('interiorRate', 'interior', inspection.interior, 'interior');
    severityInput('engineRate', 'engine', inspection.engine, 'engine');
    severityInput('tireRate', 'tires', inspection.tires, 'tyre');
    if (facts.fuel === 'HYBRID' || facts.fuel === 'EV') {
        severityInput('hybridRate', 'hybrid', inspection.hybrid, 'hybrid or EV');
    }
    else {
        set('hybridRate', 0, 'NOT_APPLICABLE', 'No hybrid assessment is required for this powertrain');
    }
    set('damageGroup1', template.damageGroup1[inspection.damageGroup1Index ?? 0] ?? 0, 'VEHICLE', 'One priced selection from damage group 1');
    set('damageGroup2', template.damageGroup2[inspection.damageGroup2Index ?? 0] ?? 0, 'VEHICLE', 'One priced selection from damage group 2');
    // ── applicability flags ───────────────────────────────────────────────────────────────────────
    if (facts.faceliftApplies === null || facts.faceliftApplies === undefined) {
        miss('faceliftRate', 'faceliftApplies', 'Whether a facelift supersedes this vehicle has not been answered');
    }
    else {
        set('faceliftRate', facts.faceliftApplies ? params.faceliftRate : 0, facts.faceliftApplies ? 'TEMPLATE' : 'VEHICLE', facts.faceliftApplies ? undefined : 'Facelift does not apply to this vehicle');
    }
    if (facts.customsApplies === null || facts.customsApplies === undefined) {
        miss('customsImpactRate', 'customsApplies', 'Whether the customs market impact applies has not been answered');
    }
    else {
        set('customsImpactRate', facts.customsApplies ? params.customsImpactRate : 0, facts.customsApplies ? 'TEMPLATE' : 'VEHICLE', facts.customsApplies ? undefined : 'The customs market impact does not apply to this vehicle');
    }
    // ── inclusions ────────────────────────────────────────────────────────────────────────────────
    set('spareKeyValue', facts.hasSpareKey ? params.spareKeyValue : 0, 'VEHICLE');
    set('ownersManualValue', facts.hasOwnersManual ? params.ownersManualValue : 0, 'VEHICLE');
    set('toolKitValue', facts.hasToolKit ? params.toolKitValue : 0, 'VEHICLE');
    set('spareTireValue', facts.hasSpareTire ? params.spareTireValue : 0, 'VEHICLE');
    // ── template shared values ────────────────────────────────────────────────────────────────────
    for (const key of [
        'depYear1',
        'depYear2',
        'depYear3',
        'depYear4',
        'annualMileageAllowance',
        'mileageStepKm',
        'mileageStepRate',
        'ownerAllowance',
        'ownerStepRate',
        'roundingIncrement',
        'registrationThresholdDays',
        'licensingRate',
        'ownershipTransferRate',
        'insuranceRate',
        'insuranceCap',
        'overheadRate',
        'fuelPricePerLiter',
        'dailyHoldingRate',
        'generalFee',
        'registrationFee',
        'commissionFee',
        'fuelLiters',
        'expectedHoldingDays',
        'targetProfit',
        'targetMargin',
        'riskBuffer',
    ]) {
        fromTemplate(key);
    }
    if (inputs.targetMargin !== undefined && inputs.targetMargin >= 1) {
        blocking.push({ code: 'INVALID_MARGIN', field: 'targetMargin', detail: 'A target gross margin of 100% or more cannot produce a retail price' });
    }
    // ── commercial facts ──────────────────────────────────────────────────────────────────────────
    set('useGrossMargin', facts.profitMode === 'MARGIN' ? 1 : 0, 'VEHICLE');
    set('taxTrafficEstimate', facts.taxTrafficEstimate ?? 0, 'VEHICLE');
    set('otherDirectCostEstimate', facts.otherDirectCostEstimate ?? 0, 'VEHICLE');
    if ((facts.taxTrafficEstimate ?? 0) < 0 || (facts.otherDirectCostEstimate ?? 0) < 0) {
        blocking.push({ code: 'NEGATIVE_COST_ESTIMATE', field: 'costEstimates', detail: 'A cost estimate cannot be negative' });
    }
    // ── cost items and the platform's own inputs ──────────────────────────────────────────────────
    const costItems = bundle.costItems ?? [];
    for (const item of costItems) {
        if ((item.estimate ?? 0) < 0 || (item.actualToDate ?? 0) < 0 || (item.remainingEstimate ?? 0) < 0) {
            blocking.push({ code: 'NEGATIVE_COST_ESTIMATE', field: `costItem:${item.id}`, detail: 'A cost item amount cannot be negative' });
        }
    }
    const costResolutions = applyManualRegistrationTransfer(resolveCosts(costItems), facts.registrationTransferFee);
    if ((facts.registrationTransferFee ?? 0) < 0) {
        blocking.push({
            code: 'NEGATIVE_COST_ESTIMATE',
            field: 'registrationTransferFee',
            detail: 'The registration and transfer fees cannot be negative',
        });
    }
    set('reconditioningResolved', roundToScale(resolvedReconditioning(costResolutions)), 'SYSTEM');
    if (bundle.owner === 'ESTIMATE') {
        // An estimate is an opportunity, not stock: nothing has been bought and nothing is standing.
        set('daysInStock', 0, 'SYSTEM', 'An estimate holds no stock');
        set('isSold', 0, 'SYSTEM');
        set('acquisitionBasis', facts.ctaOffer ?? 0, facts.ctaOffer === null || facts.ctaOffer === undefined ? 'SYSTEM' : 'VEHICLE', facts.ctaOffer === null || facts.ctaOffer === undefined
            ? 'No offer recorded yet — run the policy twice (runForOffer) to price at the ceiling'
            : 'CTA’s recorded offer');
    }
    else {
        const purchasedAt = parseIsoDate(facts.purchaseDate ?? null);
        const soldAt = parseIsoDate(facts.saleDate ?? null);
        const sold = facts.isSold === true || soldAt !== null;
        if (purchasedAt === null) {
            blocking.push({ code: 'INVALID_DATE', field: 'purchaseDate', detail: 'A stock vehicle must have a valid purchase date' });
        }
        else {
            const until = sold && soldAt !== null ? soldAt : (valuationAt ?? purchasedAt);
            set('daysInStock', Math.max(0, daysBetween(purchasedAt, until)), 'SYSTEM', sold ? 'Frozen at the sale date' : 'Since the purchase date');
        }
        set('isSold', sold ? 1 : 0, 'SYSTEM');
        if (facts.purchasePrice === null || facts.purchasePrice === undefined) {
            blocking.push({ code: 'MISSING_ACQUISITION_BASIS', field: 'purchasePrice', detail: 'A stock vehicle must have a purchase price' });
        }
        else {
            set('acquisitionBasis', facts.purchasePrice, 'VEHICLE', 'The actual purchase price is authoritative after acquisition');
        }
    }
    // ── trade-in ──────────────────────────────────────────────────────────────────────────────────
    const tradeIn = facts.acquisitionType === 'TRADE_IN' && bundle.tradeIn !== null && bundle.tradeIn !== undefined;
    if (tradeIn && bundle.tradeIn) {
        const trade = bundle.tradeIn;
        set('newVehicleMargin', trade.newVehicleMargin, 'TRADE_IN');
        set('newVehicleProfit', trade.newVehicleProfit, 'TRADE_IN');
        set('demandFactor', trade.demandLevel === null ? undefined : params[demandParamKey(trade.demandLevel)], 'TRADE_IN');
        set('stockFactor', trade.stockLevel === null ? undefined : params[stockParamKey(trade.stockLevel)], 'TRADE_IN');
        set('supportCap', trade.supportCap ?? params.supportCap, trade.supportCap === null || trade.supportCap === undefined ? 'TEMPLATE' : 'TRADE_IN');
        for (const [name, field, label] of [
            ['newVehicleMargin', 'newVehicleMargin', 'new-vehicle margin'],
            ['newVehicleProfit', 'newVehicleProfit', 'new-vehicle profit'],
            ['demandFactor', 'demandLevel', 'used-car demand level'],
            ['stockFactor', 'stockLevel', 'used-car stock level'],
        ]) {
            if (inputs[name] === undefined) {
                miss(name, field, `The ${label} is not recorded; supported trade-in economics stay blocked until it is (PP §7.4)`);
            }
        }
    }
    return { inputs, provenance, missing, blocking, costResolutions, tradeIn };
};
/** The id the typed amount carries in the cost trace, so the breakdown can say where it came from. */
export const MANUAL_REGISTRATION_TRANSFER_ID = 'manual:registrationTransferFee';
/**
 * The typed registration-and-transfer amount, as two cost resolutions (plan §11.19).
 *
 * It rides the cost pipeline rather than a new equation on purpose: the runner already replaces a cost
 * equation's figure with a category's resolution, for **every** policy version — including the ones
 * open estimates still cite — so the typed amount prices the same way whichever equations are live.
 * `REGISTRATION` carries the whole amount and `TRANSFER` resolves to zero, so the fee is counted once.
 * A category that already has recorded cost items is left alone: an invoice beats a typed guess.
 */
export const applyManualRegistrationTransfer = (resolutions, amount) => {
    if (amount === null || amount === undefined || !Number.isFinite(amount) || amount < 0) {
        return resolutions;
    }
    if (resolutions.REGISTRATION || resolutions.TRANSFER) {
        return resolutions;
    }
    return {
        ...resolutions,
        REGISTRATION: {
            category: 'REGISTRATION',
            amount: roundToScale(amount),
            items: [
                {
                    id: MANUAL_REGISTRATION_TRANSFER_ID,
                    name: 'Registration and transfer fees (typed for this vehicle)',
                    category: 'REGISTRATION',
                    status: 'ESTIMATED',
                    amount: roundToScale(amount),
                    basis: 'ESTIMATE',
                    provisional: true,
                },
            ],
            provisional: true,
            source: 'MANUAL',
        },
        TRANSFER: { category: 'TRANSFER', amount: 0, items: [], provisional: false, source: 'MANUAL' },
    };
};
const demandParamKey = (level) => `demand${level.charAt(0)}${level.slice(1).toLowerCase()}`;
const stockParamKey = (level) => `stock${level.charAt(0)}${level.slice(1).toLowerCase()}`;
//# sourceMappingURL=build-inputs.js.map